import { createFileRoute } from '@tanstack/react-router';
import { PackageSearch, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { LocaleCode, Money } from '@nokowazzi/types';
import { TRADE_DIRECTIONS } from '@nokowazzi/types';
import {
  Button,
  CategoryChips,
  EmptyState,
  InfiniteList,
  RequestCard,
  SearchBar,
  SegmentedControl,
} from '@nokowazzi/ui';
import type { RequestCardLabels } from '@nokowazzi/ui';
import { convertForViewer, useFxRates } from '../lib/fx';
import { useAuthStore } from '../stores/auth';
import { localizedText, useCategories, useRequestsInfinite } from '../features/sourcing/api';
import type { RequestListItemDto } from '../features/sourcing/api';
import { categoryLabel, formatRelativeTime } from '../features/sourcing/format';
import { requestImageUrl } from '../features/sourcing/urls';
import { useViewerCurrency } from '../features/sourcing/viewer';

const sourcingSearchSchema = z.object({
  q: z.string().min(1).max(100).optional(),
  direction: z.enum(TRADE_DIRECTIONS).optional(),
  categoryId: z.string().uuid().optional(),
});

type SourcingSearch = z.infer<typeof sourcingSearchSchema>;

export const Route = createFileRoute('/_tabs/sourcing')({
  validateSearch: (search: Record<string, unknown>): SourcingSearch => {
    const parsed = sourcingSearchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: SourcingScreen,
});

type DirectionFilter = 'ALL' | (typeof TRADE_DIRECTIONS)[number];

function SourcingScreen() {
  const { t, i18n } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const session = useAuthStore((state) => state.session);

  const [queryText, setQueryText] = useState(search.q ?? '');
  useEffect(() => {
    setQueryText(search.q ?? '');
  }, [search.q]);

  const updateSearch = (patch: Partial<SourcingSearch>) => {
    void navigate({
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
    });
  };

  const categoriesQuery = useCategories();
  const requestsQuery = useRequestsInfinite({
    q: search.q,
    direction: search.direction,
    categoryId: search.categoryId,
  });
  const fxRates = useFxRates();
  const viewerCurrency = useViewerCurrency();

  const locale = i18n.language;
  const categoryChips = useMemo(
    () =>
      (categoriesQuery.data ?? []).map((category) => ({
        id: category.id,
        label: categoryLabel(category.name_i18n, locale),
      })),
    [categoriesQuery.data, locale],
  );

  const items = useMemo(
    () => requestsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [requestsQuery.data],
  );

  const cardLabels: RequestCardLabels = {
    direction: { krToJp: t('direction.krToJp'), jpToKr: t('direction.jpToKr') },
    offers: t('sourcing.offersLabel'),
  };

  const viewerLocale: LocaleCode = locale.startsWith('ja') ? 'ja' : 'ko';

  const renderCard = (item: RequestListItemDto) => {
    const budget: Money = { amount: item.budget_amount, currency: item.budget_currency };
    const displayTitle = localizedText(
      item.title,
      item.source_lang,
      item.translations,
      viewerLocale,
      (tr) => tr.title,
    ).text;
    return (
      <RequestCard
        request={{
          id: item.id,
          title: displayTitle,
          images: item.images.map(requestImageUrl),
          direction: item.direction,
          budget,
          convertedBudget: convertForViewer(budget, viewerCurrency, fxRates.data),
          category:
            item.category === null ? undefined : categoryLabel(item.category.name_i18n, locale),
          quantity: item.quantity,
          city: item.city ?? undefined,
          offerCount: item.offer_count,
          createdAtLabel: formatRelativeTime(item.created_at, t),
          status: item.status,
        }}
        labels={cardLabels}
        onClick={(id) => {
          void navigate({ to: '/sourcing/$requestId', params: { requestId: id } });
        }}
      />
    );
  };

  const handleNewRequest = () => {
    if (session !== null) {
      void navigate({ to: '/sourcing/new' });
    } else {
      void navigate({ to: '/auth/signin', search: { redirect: '/sourcing/new' } });
    }
  };

  return (
    <div className="flex flex-col">
      <header className="flex flex-col gap-3 px-5 pt-[calc(var(--safe-top)+16px)] pb-1">
        <h1 className="text-title1 text-fg">{t('sourcing.title')}</h1>
        <SearchBar
          value={queryText}
          onValueChange={(value) => {
            setQueryText(value);
            if (value === '' && search.q !== undefined) updateSearch({ q: undefined });
          }}
          onSubmit={(value) => {
            const trimmed = value.trim();
            updateSearch({ q: trimmed === '' ? undefined : trimmed });
          }}
          placeholder={t('home.searchPlaceholder')}
          clearLabel={t('actions.clear')}
        />
        <SegmentedControl<DirectionFilter>
          options={[
            { value: 'ALL', label: t('common.all') },
            { value: 'KR_TO_JP', label: t('direction.krToJp') },
            { value: 'JP_TO_KR', label: t('direction.jpToKr') },
          ]}
          value={search.direction ?? 'ALL'}
          onChange={(value) =>
            updateSearch({ direction: value === 'ALL' ? undefined : value })
          }
        />
        <CategoryChips
          categories={categoryChips}
          selectedId={search.categoryId}
          onSelect={(id) => updateSearch({ categoryId: id ?? undefined })}
          allLabel={t('common.all')}
          className="-mx-5 px-5"
        />
      </header>

      {requestsQuery.isError ? (
        <EmptyState
          className="mt-8"
          icon={<PackageSearch size={48} strokeWidth={1.5} />}
          title={t('errors.generic')}
          action={
            <Button variant="tertiary" onClick={() => void requestsQuery.refetch()}>
              {t('actions.retry')}
            </Button>
          }
        />
      ) : (
        <InfiniteList
          className="gap-3 px-5 pt-2 pb-6"
          items={items}
          keyOf={(item) => item.id}
          renderItem={renderCard}
          onLoadMore={() => void requestsQuery.fetchNextPage()}
          hasMore={requestsQuery.hasNextPage}
          loading={requestsQuery.isPending || requestsQuery.isFetchingNextPage}
          loadingLabel={t('common.loading')}
          emptyState={
            <EmptyState
              className="mt-8"
              icon={<PackageSearch size={48} strokeWidth={1.5} />}
              title={t('sourcing.empty')}
              description={t('sourcing.emptyDescription')}
            />
          }
        />
      )}

      {/* 화면당 1개 primary CTA — 소싱 요청 FAB */}
      <Button
        variant="primary"
        size="md"
        leftIcon={<Plus className="size-5" aria-hidden="true" />}
        onClick={handleNewRequest}
        className="fixed right-5 bottom-[calc(72px+var(--safe-bottom))] z-30 rounded-full shadow-raised"
      >
        {t('sourcing.newRequest')}
      </Button>
    </div>
  );
}
